import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Box, Container } from "@mui/material";

// Public Pages
import Auth from "app/Pages/Auth";
import Info from "app/Pages/Info";
import Contact from "app/Pages/Contact";
import Terms from "app/Pages/Terms";
import HostGuide from "app/Pages/HostGuide";
import AuthActions from "app/Pages/AuthActions";
import Page404 from "app/Pages/Errors/NotFoundPage";

// Protected Pages
import Home from "app/Pages/Home";
import Messages from "app/Pages/Messages";
import ConversationLog from "app/Pages/ConversationLog";
import Settings from "app/Pages/Settings";
import GameDetails from "app/Pages/GameDetails";
import Profile from "app/Pages/Profile";
import PlayerSessions from "app/Pages/Sessions";

// Host-Only Pages
import HostPanel from "app/Pages/HostPanel";
import CreateGame from "app/Pages/HostPanel/sections/CreateGame";
import ManageGame from "app/Pages/HostPanel/sections/ManageGame/index";
import HostSessions from "app/Pages/Host/Sessions";

// Route Components
import ProtectedRoute from "app/Components/Routes/ProtectedRoute";
import HostRoute from "app/Components/Routes/HostRoute";

// Layout Components
import Navbar from "./Navbar";
import Footer from "./Footer";
import LocationBanner from "../Components/LocationBanner";

interface RouterProps {
  msgsCount?: {
    numMsgs: number;
    unreadMsgs: number;
    unreadMsg?: boolean;
  } | null;
}

const Router: React.FC<RouterProps> = ({ msgsCount }) => {
  // Public routes - no authentication required
  const publicRoutes = (
    <>
      <Route path="/authactions/*" element={<AuthActions />} />
      <Route path="/authactions" element={<AuthActions />} />
      <Route path="/info" element={<Info />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/hostguide" element={<HostGuide />} />
      <Route path="/" element={<Auth />} />
    </>
  );

  // Protected routes - authentication required
  const protectedRoutes = (
    <>
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages numMsgs={msgsCount?.numMsgs || 0} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversation/:userId"
        element={
          <ProtectedRoute>
            <ConversationLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/games/:gameId"
        element={
          <ProtectedRoute>
            <GameDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/:userId"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/playerSessions"
        element={
          <ProtectedRoute>
            <PlayerSessions />
          </ProtectedRoute>
        }
      />
    </>
  );

  // Host-only routes - requires host privileges
  const hostRoutes = (
    <>
      <Route
        path="/hostpanel"
        element={
          <HostRoute>
            <HostPanel />
          </HostRoute>
        }
      />
      <Route
        path="/creategame"
        element={
          <HostRoute>
            <CreateGame />
          </HostRoute>
        }
      />
      <Route
        path="/editgame/:gameId"
        element={
          <HostRoute>
            <CreateGame />
          </HostRoute>
        }
      />
      <Route
        path="/manage/:gameId"
        element={
          <HostRoute>
            <ManageGame />
          </HostRoute>
        }
      />
      <Route
        path="/hostSessions"
        element={
          <HostRoute>
            <HostSessions />
          </HostRoute>
        }
      />
    </>
  );

  // Error routes
  const errorRoutes = (
    <>
      <Route path="/404" element={<Page404 />} />
      <Route path="*" element={<Page404 />} />
    </>
  );

  return (
    <BrowserRouter>
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        <Navbar msgsCount={msgsCount} />
        <LocationBanner />
        <Box>
          <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
            <Routes>
              {publicRoutes}
              {protectedRoutes}
              {hostRoutes}
              {errorRoutes}
            </Routes>
          </Container>
        </Box>
        <Footer />
        <Box 
          sx={{ 
            bgcolor: 'white',
            flexGrow: 1,
            minHeight: 0
          }} 
        />
      </Box>
    </BrowserRouter>
  );
};

export default Router;
